/**
 * 이름·입력 정책 (#282) — 프로젝트/워크스페이스 이름과 위저드 입력의
 * 길이 상한과 중복 판정 규칙을 한곳에서 정의한다.
 *
 * 중복 판정은 정규화 비교: 공백 전부 제거 + 대소문자 무시. 저장은 사용자가
 * 입력한 원형 그대로 하고, 비교만 정규화한다 — "MyProject"와 "myproject "를
 * 다른 이름으로 취급해 생기는 검색·식별 혼란(QA 2026-08-07)을 막는다. 가운데 공백도
 * 지운다 — "기본 워크스페이스" 옆에 "기본워크스페이스" 가 서는 것처럼 눈으로 못 가르는
 * 이름을 다른 이름으로 치지 않으려는 것이고, 화면 문구 «(공백·대소문자 무시)» 가 약속하는
 * 그대로다(스테이지 베타 2026-10-14).
 *
 * 이 규칙은 **서버와 같은 규칙**이다(bff 0019): 선언은 BFF proto 의 `Project.name`·`Workspace.name` 주석, 적합성 벡터는
 * `proto/pm/name_key_vectors.json`(proto 와 함께 받는 사본) — `normalizeName` 과 BFF `name_key.py` 가 같은 벡터를 통과한다
 * (`namePolicy.vectors.test`). 서버가 유일성을 들므로(워크스페이스는 사용자 안, 프로젝트는 워크스페이스 안) 여기 검사는
 * 사전검사이고, 서버의 거절(ALREADY_EXISTS)도 같은 문구로 말한다.
 */

import { QUOTE_DELIMITER_TEXT, startsQuoted } from '@/domain/marsQuotedWords';
import { withParticle } from '@/shared/koreanParticle';

export const PROJECT_TITLE_MAX = 60;
export const PROJECT_DESCRIPTION_MAX = 500;
export const WORKSPACE_NAME_MAX = 50;
/** 모델 이름 상한 — 참가자 이름·Modelica 클래스 이름·산출물 파일명이 여기서 파생된다(프로젝트 제목과 같은 60).
 *  커플링에서는 종류의 엔진이 더 짧은 preCICE 이름 한도를 선언할 수 있다(결선 계약 `preciceNameMaxLength` — 결선 검증이 잰다) */
export const MODEL_NAME_MAX = 60;

/** 비교용 정규화(= 서버의 이름 키) — 저장값을 바꾸지 않는다. 공백(JS `\s` 의 25자, 앞뒤·가운데 전부)을 지우고 소문자로 */
export function normalizeName(name: string): string {
  return name.replace(/\s+/g, '').toLowerCase();
}

/** 정규화 기준 중복 여부 — 이름 변경 검사는 호출부에서 자기 자신을 목록에서 빼고 넘긴다 */
export function isDuplicateName(name: string, existing: readonly string[]): boolean {
  const target = normalizeName(name);
  if (!target) return false;
  return existing.some((e) => normalizeName(e) === target);
}

/** 위 규칙(normalizeName)을 사용자에게 설명하는 말 — 규칙과 함께 여기 한 곳. 규칙을 바꾸면 이 말도 같이 바꾼다 */
export const DUPLICATE_NAME_RULE_NOTE = '공백·대소문자 무시';

/** 이름 중복 거절 문구의 주어 — 무엇의 이름인지(조사까지) */
const DUPLICATE_NAME_SUBJECT = { workspace: '워크스페이스가', project: '프로젝트가', model: '모델이' } as const;

/** 이름 중복 거절 문구 — 사전검사와 서버 거절(ALREADY_EXISTS) 모두 이것을 쓴다(규칙이 하나라 설명도 하나) */
export function duplicateNameMessage(subject: keyof typeof DUPLICATE_NAME_SUBJECT): string {
  return `같은 이름의 ${DUPLICATE_NAME_SUBJECT[subject]} 이미 있습니다 (${DUPLICATE_NAME_RULE_NOTE}).`;
}

/** 이름 종류의 선언 — 이름표와 길이 상한. 필수·길이·중복의 판정은 `validateName` 하나가 이 표를 읽는다(D-126) */
const NAME_KINDS: Record<keyof typeof DUPLICATE_NAME_SUBJECT, { label: string; max: number }> = {
  workspace: { label: '워크스페이스 이름', max: WORKSPACE_NAME_MAX },
  project: { label: '프로젝트 이름', max: PROJECT_TITLE_MAX },
  model: { label: '모델 이름', max: MODEL_NAME_MAX },
};
export type NameKind = keyof typeof NAME_KINDS;

/** 저장하는 이름 — 앞뒤 공백을 뗀다. 판정(validateName)도 뗀 이름을 재고, 이름을 받는 표면은 모두 이것을 저장한다 */
export const storedName = (name: string): string => name.trim();

/**
 * 이름 규칙 — 비어 있지 않고, 상한 안이고, 같은 범위 안에서 유일(위의 정규화 비교). 워크스페이스(만들기·이름 바꾸기), 프로젝트(새 프로젝트
 * 마법사·JSON 가져오기), 모델(새 모델 창·모델 편집 창·JSON 가져오기의 모델)이 같은 판정을 쓴다 — 전에는 프로젝트·워크스페이스의 필수·길이를
 * 표면마다 따로 적었다(문장도 둘). 입력 칸의 maxLength 는 보조이고 규칙은 여기 한 곳(붙여넣기·프로그램 경로도 같은 상한).
 * `takenNames` 는 **다른** 것들의 이름이다(자기 이름은 빼고 준다). 위반 사유를 돌려주고 없으면 null.
 * 모델의 참가자·산출물 파일 이름은 시작 시점에 모델 이름에서 만들므로(participantNames) 이름을 바꿔도 저장된 계약이 깨지지 않는다.
 */
export function validateName(kind: NameKind, name: string, takenNames: readonly string[]): string | null {
  name = storedName(name);
  const { label, max } = NAME_KINDS[kind];
  if (!name) return `${withParticle(label, '을')} 입력해주세요.`;
  if (name.length > max) return `${withParticle(label, '은')} ${max}자 이내여야 합니다.`;
  if (isDuplicateName(name, takenNames)) return duplicateNameMessage(kind);
  return null;
}

// ============================================================================
// MARS 카드 이름 (#372)
// ============================================================================

/**
 * MARS 카드에 문자 워드로 실리는 이름(컴포넌트 이름, 제어변수 이름 등)의
 * 바이트 상한.
 *
 * 근거는 엔진 입력 토크나이저다 — `Envrl/cvic.f90`:
 *   "if count exceeds word capacity 8 ch/word, conversion uses additional words"
 * 문자 토큰은 8바이트 = 1워드로 저장되고, 넘치면 **잘리는 게 아니라 다음
 * 워드로 흘러넘쳐** 뒤 필드를 밀어낸다. 컴포넌트 이름이 9바이트면 초과분이
 * type 자리로 들어가 "Component type NNN is illegal"이라는 엉뚱한 에러가 된다.
 *
 * 문자 수가 아니라 **바이트**인 이유: cvic 는 `CHARACTER bcd*80` 카드 이미지
 * 위에서 바이트 단위로 동작한다. "증기발생기"는 5자지만 UTF-8 15바이트라 넘친다.
 */
export const MARS_NAME_MAX_BYTES = 8;

/** 폼 필드용 안내 문구 */
export const MARS_NAME_HINT = `공백 없이 ${MARS_NAME_MAX_BYTES}바이트 이내, 따옴표(${QUOTE_DELIMITER_TEXT})로 시작하지 않음 (MARS 형식 한계, 한글 1자=3바이트)`;

/** 카드에 실제로 실리는 크기 — UTF-8 바이트 길이 */
export function marsNameByteLength(name: string): number {
  return new TextEncoder().encode(name).length;
}

export type MarsNameProblem = 'empty' | 'whitespace' | 'quote-start' | 'too-long';

/**
 * 이름이 MARS 카드 워드로 나갈 수 있는지 검사 — 문제없으면 null.
 *
 * - empty: 워드가 아예 빠져 뒤 필드(type)가 이름 자리로 당겨진다
 * - whitespace: 공백은 워드 구분자라 이름이 두 워드로 쪼개진다 — 길이와
 *   무관하게 초과와 같은 증상
 * - quote-start: 인용 구분자(`"`·`'`)로 시작 — 이름은 따옴표 없이 실리므로 엔진이 그 워드를 닫히지 않은 인용 워드로 읽어 카드를 거절한다
 *   (cvic.f90 표지 61). 구분자 선언은 덱 판독과 같은 `domain/marsQuotedWords`
 * - too-long: 8바이트 초과 — 다음 워드로 흘러넘침
 */
export function checkMarsCardName(name: string): MarsNameProblem | null {
  if (!name) return 'empty';
  if (/\s/.test(name)) return 'whitespace';
  if (startsQuoted(name)) return 'quote-start';
  if (marsNameByteLength(name) > MARS_NAME_MAX_BYTES) return 'too-long';
  return null;
}

/** 위반 사유를 사용자 문구로 */
export function marsNameProblemMessage(name: string, problem: MarsNameProblem): string {
  switch (problem) {
    case 'empty':
      return '이름이 비어 있습니다';
    case 'whitespace':
      return `"${name}" — 이름에 공백이 있습니다`;
    case 'quote-start':
      return `"${name}" — 이름이 따옴표(${QUOTE_DELIMITER_TEXT})로 시작합니다`;
    case 'too-long': {
      const bytes = marsNameByteLength(name);
      return `"${name}" — ${bytes === name.length ? `${bytes}자` : `${name.length}자(${bytes}바이트)`}`;
    }
  }
}

/**
 * 컴포넌트 폼의 "검증 실패해도 저장" 폴백에서 쓰는 이름 정책 — **이름만 하드**.
 *
 * 이 에디터의 폼은 파라미터 미완성 상태도 저장을 허용한다(작업 중간 저장).
 * 이름 필드만 예외다 — 위반 이름은 작업 중간값으로서도 쓸모가 없고 MARS 카드
 * 형식을 깨뜨린다. 그래서:
 *   - 새 이름이 적법하면: trim 해서 반영 (붙여넣기 끝 공백 무해화)
 *   - 새 이름이 위반이면: **이름만 기존 값(currentName)으로 되돌리고,
 *     나머지 편집은 전부 저장한다.** 편집 유실 없음 — 저장 직후 폼이 기존
 *     이름으로 되돌아가고(패널의 검증 알림에 zod 판정이 남는다) 캔버스
 *     라벨도 그대로인 것이 "이름은 반영 안 됨"의 피드백이다.
 *     (2026-08-24 합의, 2026-08-25 저장 후 reset 도입으로 문구 갱신)
 */
export function submitWithNameFallback<T extends { name?: string }>(
  getValues: () => T,
  currentName: string,
  submit: (values: T, zodMessages?: string[]) => void,
  zodErrors?: unknown,
): void {
  const values = getValues();
  const name = String(values.name ?? '').trim();
  const patched = { ...values, name: checkMarsCardName(name) ? currentName : name };
  const messages = flattenFieldErrors(zodErrors);
  // 판정이 없으면(에러 트리 미전달·비어 있음) 인자 자체를 생략한다.
  if (messages.length > 0) submit(patched, messages);
  else submit(patched);
}

/**
 * react-hook-form 의 FieldErrors 트리를 "경로: 메시지" 목록으로 편탄화한다.
 * 검증 실패 저장(작업 중간 저장)에서도 zod 의 판정을 컴포넌트 status/errors 로
 * 전달하기 위한 것 — 판정은 zod 한 곳이고, 그 판정은 버려지지 않는다 (#373).
 */
export function flattenFieldErrors(errors: unknown, path = ''): string[] {
  if (!errors || typeof errors !== 'object') return [];
  const out: string[] = [];
  for (const [key, v] of Object.entries(errors as Record<string, unknown>)) {
    if (key === 'ref' || v === null || v === undefined) continue;
    const here = path ? `${path}.${key}` : key;
    if (typeof v === 'object' && 'message' in (v as object) && typeof (v as { message?: unknown }).message === 'string') {
      out.push(`${here}: ${(v as { message: string }).message}`);
    } else if (typeof v === 'object') {
      out.push(...flattenFieldErrors(v, here));
    }
  }
  return out;
}

/**
 * 복사본 이름 파생 — 항상 8바이트 이내이고, 원본·기존 이름과 겹치지 않는다.
 *
 * `_c`, `_c2`, … 접미사를 붙이되 바이트가 넘치면 원본 꼬리를 줄인다.
 * 원본이 이미 `…_c` 꼴이라 후보가 원본과 같아지는 고정점이 있으므로
 * (예: "abcdef_c" → "abcdef"+"_c" = 원본) 겹침 검사로 다음 접미사로 넘어간다.
 * 전부 실패하면 fallback(호출부가 보장하는 유일 이름, 예: "cv17")을 쓴다.
 */
export function deriveCopyName(
  original: string,
  taken: readonly string[],
  fallback: string,
): string {
  const takenSet = new Set(taken);
  for (let i = 1; i < 100; i++) {
    const suffix = i === 1 ? '_c' : `_c${i}`;
    let base = original.trim();
    while (base.length > 0 && marsNameByteLength(base + suffix) > MARS_NAME_MAX_BYTES) {
      base = base.slice(0, -1);
    }
    const candidate = base + suffix;
    if (base.length > 0 && candidate !== original && !takenSet.has(candidate) && !checkMarsCardName(candidate)) {
      return candidate;
    }
  }
  return fallback;
}
